import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  resource,
  signal,
  untracked,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SimpleApi } from '@core/api/simple.api';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { LetDatePickerComponent } from '@shared/let-datepicker/let-date-picker.component';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { BatchJobScheduleDatasource } from '../batch-job-schedule/batch-job-schedule.datasource';
import { BatchJobSchedule } from '../batch-job-schedule/batch-job-schedule.model';
import { BatchJobTypeParameterApi } from '../batch-job-type-parameter.api';
import { BatchJobTypeParameter } from '../batch-job-type-parameter.model';
import { BatchJobTypeDatasource } from '../batch-job-type.datasource';
import { BatchJobScheduleParameterApi } from './batch-job-schedule.parameter.api';
import { ScrollPanel } from 'primeng/scrollpanel';
import { BatchJobParameter } from '../batch-job-detail/batch-job-parameter.model';
import dayjs from 'dayjs';
import { BatchService } from '../batch.service';
import { Checkbox } from 'primeng/checkbox';

@Component({
  selector: 'app-batch-job-schedule-detail',
  imports: [
    DatePipe,
    Skeleton,
    InputTextModule,
    ReactiveFormsModule,
    SelectModule,
    LetDatePickerComponent,
    RadioButton,
    Checkbox,
    InputNumber,
    Button,
    ScrollPanel,
  ],
  templateUrl: './batch-job-schedule-detail.component.html',
  styleUrl: './batch-job-schedule-detail.component.css',
})
export class BatchJobScheduleDetailComponent {
  batchJobSchedule = input<BatchJobSchedule>();
  closeWindowRequest = output();
  authService = inject(AuthService);
  simpleApi = inject(SimpleApi);
  batchService = inject(BatchService);

  batchJobParameterApi = inject(BatchJobScheduleParameterApi);
  batchJobTypeParameterApi = inject(BatchJobTypeParameterApi);
  batchJobTypeDatasource = inject(BatchJobTypeDatasource);
  batchJobScheduleDatasource = inject(BatchJobScheduleDatasource);
  availableParameters = signal<BatchJobTypeParameter[]>([]);

  constructor() {
    //Loads form with batchJobSchedule data
    effect(() => {
      const job = this.batchJobSchedule();
      if (job) {
        this.form.patchValue(job);
      }
    });

    //Loads form with batchJobSchedule parameter values
    effect(() => {
      const params = this.parameterValues.value()?.data || [];
      const parameterValueObject: Record<string, string> = {};
      params.forEach(pv => {
        parameterValueObject[pv.parameterNavn] = pv.parameterVaerdi;
      });
      const paramFormGroup = this.form.get('parameters') as FormGroup;
      paramFormGroup.patchValue(parameterValueObject);
    });

    effect(() => {
      const batchJobTypeId = this.batchJobSchedule()?.batchJobTypeId;
      if (batchJobTypeId) {
        untracked(async () => await this.loadParameters(batchJobTypeId));
      }
    });
  }

  parameterValues = resource({
    params: () => this.batchJobSchedule()?.id,
    loader: async ({ params, abortSignal }) => {
      return await this.batchJobParameterApi.getList(
        {
          filter: JSON.stringify({
            batchJobScheduleId: params,
          }),
          sortBy: 'parameterNavn',
        },
        abortSignal
      );
    },
  });

  batchJobTypes = this.batchJobTypeDatasource.data;

  koeresEfterOptions = computed(() => {
    const apiResponse = this.batchJobScheduleDatasource.data();
    const list = apiResponse.data;
    return list
      .filter(
        x =>
          x.frekvens !== 'Engangskoersel' &&
          x.id !== this.batchJobSchedule()?.id
      )
      .sort((a, b) => (a.jobNavn > b.jobNavn ? 1 : -1));
  });

  form: FormGroup = new FormGroup({
    jobNavn: new FormControl('', Validators.required),
    batchJobTypeId: new FormControl('', Validators.required),
    udfoeresEfter: new FormControl(''),
    koerselsTidspunkt: new FormControl(
      dayjs().set('second', 0).set('millisecond', 0).toDate(),
      Validators.required
    ),
    frekvens: new FormControl('Engangskoersel'),
    parameters: new FormGroup({}),
  });

  resetForm() {
    this.form.reset();
    this.form.patchValue({
      frekvens: 'Engangskoersel',
      koerselsTidspunkt: dayjs()
        .set('second', 0)
        .set('millisecond', 0)
        .toDate(),
    });
    this.removeParameters();
    this.availableParameters.set([]);
  }

  removeParameters() {
    const paramFormGroup = this.form.get('parameters') as FormGroup;
    Object.keys(paramFormGroup.controls).forEach(key => {
      paramFormGroup.removeControl(key);
    });
  }

  allowEdit() {
    return this.authService.hasRettighed(RettighedsType.AdminBatchSkriv);
  }

  async onChangeBatchJobType(e: SelectChangeEvent) {
    const batchJobTypeId = e.value;

    this.removeParameters();
    const paramFormGroup = this.form.get('parameters') as FormGroup;
    Object.keys(paramFormGroup.controls).forEach(key => {
      paramFormGroup.removeControl(key);
    });

    await this.loadParameters(batchJobTypeId);
  }

  async loadParameters(batchJobTypeId: number) {
    let params: BatchJobTypeParameter[] = [];

    if (batchJobTypeId) {
      const response = await this.batchJobTypeParameterApi.getList({
        filter: JSON.stringify({ batchJobTypeId: batchJobTypeId }),
      });
      params = response.data;

      //add new fields
      const paramFormGroup = this.form.get('parameters') as FormGroup;
      params.forEach(p => {
        paramFormGroup.addControl(
          p.parameterNavn,
          new FormControl(
            p.parameterVaerdi ? p.parameterVaerdi : null,
            p.paakraevet ? Validators.required : undefined
          )
        );
        paramFormGroup.addControl(
          p.parameterNavn + '_Checked',
          new FormControl(!!p.parameterVaerdi)
        );
      });
    }

    this.availableParameters.set(params);
  }

  async onClickOk() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const batchJobScheduleId = this.batchJobSchedule()?.id;

    const newParams: Partial<BatchJobParameter>[] = [];
    for (const [key, value] of Object.entries(this.form.value.parameters)) {
      if (!key.endsWith('_Checked')) {
        if (this.form.value.parameters[key + '_Checked'] === true) {
          const paramObject: Partial<BatchJobParameter> = {
            parameterNavn: key,
            parameterVaerdi: value as string,
            batchJobScheduleId: batchJobScheduleId || 0,
          };
          newParams.push(paramObject);
        }
      }
    }
    Object.entries(this.form.value.parameters);
    const newBatchJobSchedule: BatchJobSchedule = {
      id: batchJobScheduleId,
      ...this.form.value,
      koerselsTidspunkt: dayjs(this.form.value.koerselsTidspunkt)
        .set('second', 0)
        .set('millisecond', 0)
        .toDate(),
      parameters: newParams,
    };

    if (batchJobScheduleId) {
      await this.batchJobScheduleDatasource.update(
        batchJobScheduleId,
        newBatchJobSchedule
      );
    } else {
      await this.batchJobScheduleDatasource.create(newBatchJobSchedule);
    }
    this.resetForm();
    this.closeWindowRequest.emit();
  }

  onClickCancel() {
    this.resetForm();
    this.closeWindowRequest.emit();
  }

  frekvensOptions = [
    { text: 'Engangskørsel', value: 'Engangskoersel' },
    { text: 'Dagligt', value: 'Dagligt' },
    { text: 'Bankdage', value: 'Bankdage' },
    { text: 'Ugentligt', value: 'Ugentligt' },
    { text: 'Månedligt', value: 'Maanedligt' },
    { text: 'Kvartalsvis', value: 'Kvartalsvis' },
    { text: 'Årligt', value: 'Aarligt' },
    { text: 'Bankdag sidst i måneden', value: 'BankdagSidstIMaaneden' },
    { text: 'Første mandag i måneden', value: 'FoersteMandagIMaaneden' },
    { text: 'Hver time', value: 'HverTime' },
    { text: 'Sidste dag i måneden', value: 'SidsteDagIMaaneden' },
  ];
}
